import type { RecordPaymentInput } from "@lumora/bridge";
import { formatUsd } from "@lumora/core";
import { useState } from "react";
import { usePlatformPaymentsVm } from "./platformViewModels.js";
import { ConfirmGate } from "./ConfirmGate.js";
import { ProvisionCard, useActionStatus } from "./ProvisionCard.js";

/** Admin: record an offline payment → activates the site and can waive hosting. */
export function RecordPaymentPanel({
  slug,
  onRecorded,
}: {
  slug: string;
  onRecorded: () => void;
}) {
  const [amountDollars, setAmountDollars] = useState("850");
  const [method, setMethod] = useState<RecordPaymentInput["method"]>("cash");
  const [kind, setKind] = useState<RecordPaymentInput["kind"]>("build");
  const [note, setNote] = useState("");
  const [waiveDays, setWaiveDays] = useState("30");
  const status = useActionStatus();
  const vm = usePlatformPaymentsVm();

  return (
    <ProvisionCard
      title="Record offline payment"
      lead="Cash / check / wire received offline. Recording activates the site (SAMPLE off) and can waive the first hosting month."
      status={status}
    >
      <form
        className="site-signin"
        onSubmit={(e) => {
          e.preventDefault();
          status.start();
          const dollars = Number.parseFloat(amountDollars);
          if (!Number.isFinite(dollars) || dollars <= 0) {
            status.fail(null, "Enter a valid dollar amount");
            return;
          }
          const days = Number.parseInt(waiveDays, 10);
          const input: RecordPaymentInput = {
            amountCents: Math.round(dollars * 100),
            currency: "USD",
            method,
            kind,
          };
          const trimmed = note.trim();
          if (trimmed) input.note = trimmed;
          if (Number.isFinite(days)) input.waiveHostingDays = days;
          void vm.recordPayment(slug, input).then(
            (r) => {
              const waived = r.site.hostingWaivedUntil
                ? ` Hosting waived until ${new Date(r.site.hostingWaivedUntil).toLocaleDateString()}.`
                : "";
              status.ok(
                `Recorded ${formatUsd(r.payment.amountCents)} — site active, SAMPLE off.${waived}`,
              );
              onRecorded();
            },
            (e: unknown) => status.fail(e, "Failed to record payment"),
          );
        }}
      >
        <label>
          Amount (USD)
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            required
          />
        </label>
        <label>
          Method
          <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
            <option value="cash">Cash</option>
            <option value="check">Check</option>
            <option value="wire">Wire</option>
            <option value="card_offline">Card (offline)</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="build">Build fee</option>
            <option value="hosting">Hosting</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Waive hosting (days)
          <input
            type="number"
            min="0"
            step="1"
            value={waiveDays}
            onChange={(e) => setWaiveDays(e.target.value)}
          />
        </label>
        <label>
          Note
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Ben cash Oct 3"
          />
        </label>
        <ConfirmGate
          idleLabel="Record payment & activate"
          confirmLabel="Yes, record payment"
          confirmType="submit"
          onConfirm={() => {}}
        />
      </form>
    </ProvisionCard>
  );
}
